import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { RichTextEditor } from "./RichTextEditor";
import { announcementError } from "./errors";

const API = process.env.REACT_APP_BACKEND_URL;
const TITLE_MAX = 120;
const BODY_MAX = 3000;

export function AnnouncementForm({ item, onClose, onSaved }) {
  const [title, setTitle] = useState(item?.title || "");
  const [body, setBody] = useState({ html: item?.body_html || "", text: item?.body || "" });
  const [startsOn, setStartsOn] = useState(item?.starts_on || "");
  const [endsOn, setEndsOn] = useState(item?.ends_on || "");
  const [enabled, setEnabled] = useState(!!item?.enabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const datesWrong = startsOn && endsOn && endsOn < startsOn;
  const tooLong = body.text.length > BODY_MAX;
  const canSave = title.trim() && body.text.trim() && !datesWrong && !tooLong && !busy;

  const save = async () => {
    setBusy(true);
    setError(null);
    const payload = { title: title.trim(), body: body.text.trim(), body_html: body.html, enabled, starts_on: startsOn || null, ends_on: endsOn || null };
    try {
      if (item) await axios.put(`${API}/api/announcements/${item.id}`, payload);
      else await axios.post(`${API}/api/announcements`, payload);
      toast.success(item ? "Announcement updated" : "Announcement created");
      onSaved();
    } catch (e) {
      setError(announcementError(e, "Could not save this announcement"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="announcement-form">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>{item ? "Edit announcement" : "New announcement"}</DialogTitle>
          <DialogDescription>Keep it short. Clients read it once, on the loading screen, and press Continue.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <label className="block">
            <span className="text-xs font-medium text-stone-600">Title</span>
            <Input value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} placeholder="Medicys is closed from 24 December to 2 January" className="mt-1" data-testid="announcement-title-input" />
            <span className="mt-1 block text-right text-[11px] font-mono text-stone-400">{title.length}/{TITLE_MAX}</span>
          </label>
          <div>
            <span className="text-xs font-medium text-stone-600">Text</span>
            <div className="mt-1">
              <RichTextEditor
                value={body.html || (item?.body ? `<p>${item.body.replace(/\n\n+/g, "</p><p>").replace(/\n/g, "<br>")}</p>` : "")}
                onChange={setBody}
                placeholder="What the client needs to know, in a few sentences."
              />
            </div>
            <span className={`mt-1 block text-right text-[11px] font-mono ${tooLong ? "text-rose-600" : "text-stone-400"}`} data-testid="announcement-body-count">{body.text.length}/{BODY_MAX}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Show from</span>
              <Input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} className="mt-1" data-testid="announcement-starts-input" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Until</span>
              <Input type="date" value={endsOn} min={startsOn || undefined} onChange={(e) => setEndsOn(e.target.value)} className="mt-1" data-testid="announcement-ends-input" />
            </label>
          </div>
          <p className="text-[11px] text-stone-400 -mt-2">Leave both empty to show it whenever it is switched on.</p>
          {datesWrong && <p className="text-xs text-rose-600" data-testid="announcement-dates-error">The end date must not be before the start date.</p>}
          <label className="flex items-center justify-between rounded-lg border border-stone-200 bg-stone-50 px-4 py-3">
            <span>
              <span className="block text-sm font-medium text-slate-900">Switched on</span>
              <span className="block text-xs text-stone-500">Only one announcement can be on at a time.</span>
            </span>
            <Switch checked={enabled} onCheckedChange={setEnabled} className="data-[state=checked]:bg-[#1B4F8A]" data-testid="announcement-enabled-input" />
          </label>
          {error && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="announcement-form-error">{error}</p>
          )}
        </div>
        <DialogFooter className="border-t border-stone-100 bg-white px-6 py-4">
          <Button variant="outline" onClick={onClose} data-testid="announcement-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={!canSave} className="bg-[#1B4F8A] hover:bg-[#173f6e]" data-testid="announcement-form-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {item ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
